import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription, timer } from 'rxjs';
import { debounceTime, switchMap, takeWhile } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { OdooToastService } from 'src/app/services/odoo/odoo-toast.service';
import { PERMISO_ODOO_ESCRIBIR } from 'src/app/services/odoo/odoo.models';
import { mensajeDeFallo } from 'src/app/services/odoo/odoo.service';
import { OdooBitacoraService } from 'src/app/services/odoo/odoo-bitacora.service';
import {
  AvisoGeneracion, EstadoGeneracion, FiltroEstado, Generacion, OdooArchivosService, atraparFoco, chipGeneracion, diaVE, horaEntera, momento, num, rangoTexto, usd,
} from 'src/app/services/odoo/odoo-archivos.service';

/** Una fila de la tabla, ya armada (nada se calcula al pintar). */
export interface FilaGen {
  id: string;
  rango: string;
  giros: string;
  pagos: string;
  duplicados: string;
  estado: EstadoGeneracion;
  chip: string;
  tipoChip: 'solid' | 'dash' | 'problem' | 'ghost';
  pct: number;
  automatico: boolean;
  avance: string;
  /** el último envío quedó a medias: falta enviar una parte */
  envioFallido: boolean;
  /** motivo corto cuando la generación tiene un problema (error, interrumpida…) */
  motivo: string;
}

/** Un aviso de la cabecera: rojo = hay que actuar, ámbar = ojo. */
export interface AvisoVm { id: string | null; rojo: boolean; mensaje: string; accion: 'Ver' | 'Generar'; desde: string; hasta: string; }
const ROJOS = ['error', 'interrumpido_enviando', 'envio_a_medias', 'con_problemas'];
export const ESTADOS_FILTRO: { valor: FiltroEstado; texto: string }[] = [
  { valor: '', texto: 'Todos' }, { valor: 'con_problema', texto: 'Con problema' }, { valor: 'sin_enviar', texto: 'Sin enviar' }, { valor: 'enviado', texto: 'Enviado' },
];

interface VistaModal {
  desde: string;
  hasta: string;
  /** días del rango que ya se enviaron a 3MIT (AAAA-MM-DD) */
  enviados: string[];
  creando: boolean;
  error: string | null;
  /** Generación en curso o terminada que se muestra en el mismo modal */
  id: string | null;
  gen: Generacion | null;
  avance: string;
  resumen: { giros: string; pagos: string; duplicados: number; problemas: string; conProblemas: boolean } | null;
}

const MAX_DIAS = 31;
const sumarDias = (ymd: string, n: number): string => new Date(Date.parse(`${ymd}T00:00:00Z`) + n * 86400000).toISOString().slice(0, 10);
const diasEntre = (a: string, b: string): number => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000) + 1;
const cuenta = (p: unknown): number => (Array.isArray(p) ? p.length : typeof p === 'number' ? p : 0);
const filasTxt = (n: number): string => `${num(n)} ${n === 1 ? 'fila' : 'filas'}`;

/**
 * ARCHIVOS DEL DÍA — trabaja por GENERACIONES de un rango de fechas en dos pasos: generar y luego enviar al bucket.
 * Cabecera con el estado de la subida automática, modal «Generar archivos» (avance cada 3 s), tabla de generaciones
 * (se actualiza cada 5 s mientras alguna genera o envía) y el detalle en una hoja lateral (app-odoo-generacion).
 */
@Component({
  selector: 'app-odoo-archivos',
  templateUrl: './odoo-archivos.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OdooArchivosComponent implements OnInit, OnDestroy {
  readonly limite = 10;
  readonly esperando = [0, 1, 2];
  readonly maxFecha = diaVE(-1);
  page = 1;
  /** Filtro por fechas de la tabla (vive en la URL: ?desde=…&hasta=…). */
  filtroDesde = '';
  filtroHasta = '';
  filtroEstado: FiltroEstado = '';
  readonly estados = ESTADOS_FILTRO;

  cargando = true;
  error: string | null = null;
  total = 0;
  filas: FilaGen[] = [];
  reintentando: string | null = null;

  /** Estado de la subida automática y avisos de lo que hay que atender (salen de GET /generaciones/avisos). */
  automatica: boolean | null = null;
  horaSubida = 1;
  proxima = '';
  avisos: AvisoVm[] = [];
  /** motivo corto por generación (del aviso que le toca) */
  motivos: Record<string, string> = {};
  consolaUrl = '';

  modal: VistaModal | null = null;
  /** Generación abierta en la hoja lateral (null = cerrada). */
  abierta: string | null = null;

  @ViewChild('campoDesde') campoDesde?: ElementRef<HTMLInputElement>;

  private sondeoLista?: Subscription;
  private sondeoModal?: Subscription;
  private readonly cambioFechas$ = new Subject<string>();
  private cambioSub?: Subscription;
  private urlSub?: Subscription;

  constructor(private api: OdooArchivosService, private config: OdooBitacoraService, public _auth: AuthService, private toast: OdooToastService, private cdr: ChangeDetectorRef, private route: ActivatedRoute, private router: Router) {}

  ngOnInit(): void {
    const q = this.route.snapshot.queryParamMap;
    const fecha = (v: string | null): string => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : '');
    const estado = (v: string | null): FiltroEstado => (v === 'con_problema' || v === 'sin_enviar' || v === 'enviado' ? v : '');
    this.filtroDesde = fecha(q.get('desde'));
    this.filtroHasta = fecha(q.get('hasta'));
    this.filtroEstado = estado(q.get('estado'));
    this.config.config().subscribe({
      next: (c) => { this.consolaUrl = c.consola_url || ''; this.cdr.markForCheck(); },
      error: () => undefined,
    });
    // Al cambiar las fechas del modal se consultan los días ya enviados (sin repetir la misma consulta).
    this.cambioSub = this.cambioFechas$.pipe(
      debounceTime(300),
      switchMap((k) => { const [d, h] = k.split('|'); return this.api.solapes(d, h); }),
    ).subscribe({
      next: (s) => { if (this.modal && !this.modal.id) { this.modal.enviados = s.dias_enviados || []; this.cdr.markForCheck(); } },
      error: () => undefined,
    });
    // La URL manda: ?gen abre el detalle; ?desde/?hasta son el filtro (también al usar atrás/adelante del navegador).
    this.urlSub = this.route.queryParamMap.subscribe((q) => {
      const antes = this.abierta;
      this.abierta = q.get('gen') || null;
      const d = fecha(q.get('desde')), h = fecha(q.get('hasta'));
      const e = estado(q.get('estado'));
      const difiere = d !== this.filtroDesde || h !== this.filtroHasta || e !== this.filtroEstado;
      this.filtroDesde = d;
      this.filtroHasta = h;
      this.filtroEstado = e;
      if (difiere) this.page = 1;
      if (!this.abierta && (difiere || antes)) this.cargar(difiere);
      if (this.abierta) this.sondeoLista?.unsubscribe();
      this.cdr.markForCheck();
    });
    if (!this.abierta) this.cargar(true);
  }

  ngOnDestroy(): void {
    this.sondeoLista?.unsubscribe();
    this.sondeoModal?.unsubscribe();
    this.cambioSub?.unsubscribe();
    this.urlSub?.unsubscribe();
  }

  get puedeEditar(): boolean { return this._auth.hasPermission(PERMISO_ODOO_ESCRIBIR); }

  get cabecera(): string {
    if (this.automatica === null) return '';
    if (!this.automatica) return 'La subida automática está apagada: genera y envía a mano.';
    const h = horaEntera(this.horaSubida);
    return `La subida automática está encendida: cada día a ${/^1:/.test(h) ? 'la' : 'las'} ${h} se genera y se envía el día anterior${this.proxima ? ' · Próxima: ' + this.proxima : ''}`;
  }

  /** Avisos (qué atender) y estado de la subida automática. */
  private cargarAvisos(): void {
    this.api.avisos().subscribe({
      next: (r) => {
        const a = r.automatico;
        this.automatica = !!(a && a.encendido);
        this.horaSubida = a ? a.hora_subida : this.horaSubida;
        this.proxima = a && a.proxima_subida ? momento(a.proxima_subida) : '';
        this.avisos = (r.avisos || []).map((x) => this.aviso(x));
        this.motivos = {};
        for (const x of r.avisos || []) if (x.generacion_id && !this.motivos[x.generacion_id]) this.motivos[x.generacion_id] = x.mensaje;
        this.cdr.markForCheck();
      },
      error: () => undefined,
    });
  }

  private aviso(x: AvisoGeneracion): AvisoVm {
    return { id: x.generacion_id, rojo: ROJOS.includes(x.tipo), mensaje: x.mensaje, accion: x.tipo === 'falta_dia' || !x.generacion_id ? 'Generar' : 'Ver', desde: x.desde, hasta: x.hasta };
  }

  /** «Ver» abre el detalle; «Generar» (día que falta) abre el modal con ese rango ya puesto. */
  atender(a: AvisoVm): void {
    if (a.accion === 'Ver' && a.id) this.ver(a.id);
    else this.abrirModal(a.desde, a.hasta);
  }

  // ── Tabla ──
  recargar(): void { this.cargar(true); }
  ir(n: number): void { this.page = n; this.cargar(true); }

  get hayFiltro(): boolean { return !!(this.filtroDesde || this.filtroHasta || this.filtroEstado); }
  get filtroError(): string {
    return this.filtroDesde && this.filtroHasta && this.filtroHasta < this.filtroDesde ? 'La fecha final no puede ser anterior a la inicial' : '';
  }
  /** Al cambiar una fecha: se guarda en la URL, vuelve a la página 1 y recarga (si el rango es válido). */
  cambioFiltro(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { desde: this.filtroDesde || null, hasta: this.filtroHasta || null, estado: this.filtroEstado || null }, queryParamsHandling: 'merge', replaceUrl: true });
    this.page = 1;
    if (this.filtroError) { this.sondeoLista?.unsubscribe(); this.cdr.markForCheck(); return; }
    this.cargar(true);
  }
  limpiarFiltro(): void { this.filtroDesde = ''; this.filtroHasta = ''; this.filtroEstado = ''; this.cambioFiltro(); }

  private cargar(visible: boolean): void {
    this.sondeoLista?.unsubscribe();
    if (this.filtroError) return;
    if (visible) { this.cargando = true; this.error = null; this.cdr.markForCheck(); }
    this.api.lista(this.page, this.limite, this.filtroDesde, this.filtroHasta, this.filtroEstado).subscribe({
      next: (r) => {
        this.cargando = false;
        this.error = null;
        this.total = r.total;
        this.filas = r.items.map((g) => this.fila(g));
        // Los avisos se refrescan al cargar y cuando ya no hay nada generando o enviando (no en cada sondeo).
        if (visible || !r.items.some((g) => g.estado === 'generando' || g.estado === 'enviando')) this.cargarAvisos();
        this.cdr.markForCheck();
        // Mientras algo genera o envía se vuelve a consultar cada 5 segundos.
        if (r.items.some((g) => g.estado === 'generando' || g.estado === 'enviando')) {
          this.sondeoLista = timer(5000).subscribe(() => this.cargar(false));
        }
      },
      error: (e: unknown) => {
        this.cargando = false;
        if (visible || !this.filas.length) this.error = mensajeDeFallo(e);
        this.cdr.markForCheck();
      },
    });
  }

  private fila(g: Generacion): FilaGen {
    const c = g.creditos, p = g.pagos;
    const dup = p ? p.duplicados_omitidos || 0 : 0;
    const pr = g.progreso;
    const { texto: chip, tipo: tipoChip } = chipGeneracion(g);
    return {
      id: g._id, rango: rangoTexto(g.desde, g.hasta),
      giros: c ? `${filasTxt(c.filas)} · ${usd(c.total)}` : '—',
      pagos: p ? `${filasTxt(p.filas)} · ${usd(p.total)}` : '—',
      duplicados: dup > 0 ? `${dup} ${dup === 1 ? 'duplicado omitido' : 'duplicados omitidos'}` : '',
      estado: g.estado, chip, tipoChip, pct: pr ? Math.max(0, Math.min(100, Math.round(pr.pct || 0))) : 0,
      automatico: g.origen === 'automatico', avance: pr ? this.textoAvance(g) : '',
      envioFallido: g.estado === 'error' && (g.envios || []).length > 0 && !g.envios[g.envios.length - 1].ok,
      motivo: g.estado === 'error' || g.estado === 'interrumpido' ? g.error || '' : '',
    };
  }

  reintentar(f: FilaGen): void {
    if (!this.puedeEditar || this.reintentando) return;
    this.reintentando = f.id;
    this.api.regenerar(f.id, 'Reintento desde la tabla').subscribe({
      next: () => { this.reintentando = null; this.toast.mostrar('Se está generando de nuevo.'); this.cargar(false); },
      error: (e: unknown) => { this.reintentando = null; this.toast.mostrar(mensajeDeFallo(e), false); this.cdr.markForCheck(); },
    });
  }

  /** «Ver»: la pantalla de detalle vive en la URL (?gen=<id>); el filtro de fechas se conserva. */
  ver(id: string): void { this.router.navigate([], { relativeTo: this.route, queryParams: { gen: id }, queryParamsHandling: 'merge' }); }
  volver(): void { this.router.navigate([], { relativeTo: this.route, queryParams: { gen: null }, queryParamsHandling: 'merge' }); }

  // ── Modal «Generar archivos» ──
  abrirModal(desde = '', hasta = ''): void {
    if (!this.puedeEditar) return;
    this.modal = { desde: desde || this.maxFecha, hasta: hasta || desde || this.maxFecha, enviados: [], creando: false, error: null, id: null, gen: null, avance: '', resumen: null };
    this.consultarSolapes();
    this.cdr.markForCheck();
    setTimeout(() => this.campoDesde?.nativeElement.focus());
  }

  /** La X cierra el modal en cualquier momento; si ya está generando, sigue en segundo plano. */
  cerrarModal(): void {
    const m = this.modal;
    this.sondeoModal?.unsubscribe();
    this.modal = null;
    this.cdr.markForCheck();
    if (m && m.id) this.cargar(false);
  }

  get rangoError(): string {
    const m = this.modal;
    if (!m || !m.desde || !m.hasta) return 'Elige las dos fechas.';
    if (m.desde > m.hasta) return 'La fecha de inicio no puede ser después de la final.';
    if (m.hasta > this.maxFecha) return 'Solo se pueden generar días ya cerrados (hasta ayer).';
    if (diasEntre(m.desde, m.hasta) > MAX_DIAS) return `El rango no puede pasar de ${MAX_DIAS} días.`;
    return '';
  }

  cambioFecha(): void { if (this.modal) { this.modal.enviados = []; this.consultarSolapes(); } }
  private consultarSolapes(): void {
    const m = this.modal;
    if (m && !this.rangoError) this.cambioFechas$.next(`${m.desde}|${m.hasta}`);
  }

  get enviadosTexto(): string { return this.modal ? this.modal.enviados.map((d) => rangoTexto(d, d)).join(', ') : ''; }

  /** Cuando hay días ya enviados: mover el inicio al día siguiente del último enviado. */
  get siguienteDelUltimo(): string {
    const m = this.modal;
    if (!m || !m.enviados.length) return '';
    const s = sumarDias([...m.enviados].sort().pop() as string, 1);
    return s <= m.hasta ? s : '';
  }
  empezarDespues(): void {
    const s = this.siguienteDelUltimo;
    if (!this.modal || !s) return;
    this.modal.desde = s;
    this.cambioFecha();
  }

  generar(): void {
    const m = this.modal;
    if (!this.puedeEditar || !m || m.creando || m.id || this.rangoError) return;
    m.creando = true;
    m.error = null;
    this.api.crear(m.desde, m.hasta).subscribe({
      next: (r) => {
        m.creando = false;
        m.id = r.id;
        m.avance = 'Empezando…';
        this.cdr.markForCheck();
        this.seguir(r.id);
        this.cargar(false);
      },
      error: (e: unknown) => {
        m.creando = false;
        m.error = mensajeDeFallo(e);
        this.cdr.markForCheck();
      },
    });
  }

  /** Avance cada 3 segundos, hasta que deje de estar «generando» (o se cierre el modal). */
  private seguir(id: string): void {
    this.sondeoModal?.unsubscribe();
    this.sondeoModal = timer(0, 3000).pipe(
      switchMap(() => this.api.detalle(id)),
      takeWhile((g) => g.estado === 'generando', true),
    ).subscribe({
      next: (g) => {
        const m = this.modal;
        if (!m || m.id !== id) return;
        m.gen = g;
        m.avance = this.textoAvance(g);
        if (g.estado !== 'generando') {
          const dup = g.pagos ? g.pagos.duplicados_omitidos || 0 : 0;
          const n = cuenta(g.creditos && g.creditos.problemas) + cuenta(g.pagos && g.pagos.problemas);
          m.resumen = {
            giros: g.creditos ? `${filasTxt(g.creditos.filas)} · ${usd(g.creditos.total)}` : '—',
            pagos: g.pagos ? `${filasTxt(g.pagos.filas)} · ${usd(g.pagos.total)}` : '—',
            duplicados: dup, conProblemas: n > 0, problemas: n > 0 ? `${n} ${n === 1 ? 'problema' : 'problemas'}` : '✓',
          };
          this.cargar(false);
        }
        this.cdr.markForCheck();
      },
      error: () => undefined,
    });
  }

  private textoAvance(g: Generacion): string {
    const p = g.progreso;
    if (!p) return 'Generando…';
    const dia = Math.min(p.dias_total, (p.dias_hechos || 0) + 1);
    if (p.paso === 'giros') return `Generando giros… día ${dia} de ${p.dias_total}`;
    if (p.paso === 'pagos') return `Generando pagos… día ${dia} de ${p.dias_total}`;
    if (p.paso === 'revisando') return 'Revisando…';
    return 'Generando…';
  }

  get modalTerminado(): boolean { return !!this.modal && !!this.modal.gen && this.modal.gen.estado !== 'generando'; }
  get modalFallo(): string {
    const g = this.modal && this.modal.gen;
    if (!g) return '';
    if (g.estado === 'error') return g.error || 'No se pudo generar. Intenta de nuevo.';
    if (g.estado === 'interrumpido') return 'La generación se interrumpió. Pulsa «Reintentar» en la tabla.';
    return '';
  }
  verDesdeModal(): void {
    const id = this.modal && this.modal.id;
    this.cerrarModal();
    if (id) this.ver(id);
  }

  @HostListener('document:keydown.escape') alEscape(): void { if (this.modal && !this.abierta) this.cerrarModal(); }
  atrapar(ev: KeyboardEvent, dialogo: HTMLElement): void { atraparFoco(ev, dialogo); }

  porId = (_: number, f: FilaGen): string => f.id;
  porIndice = (i: number): number => i;
  porValor = (_: number, e: { valor: string }): string => e.valor;
}
